import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { FiEdit2, FiArchive, FiSearch } from "react-icons/fi";

import AccountService from "src/Services/API/account.service";
import BranchService from "src/Services/API/branch.service";
import CustomerService from "src/Services/API/customer.service";
import { setCustomerName } from "src/Redux/Slices/HeaderSlice";
import { formatDate, getImageUrl } from "src/Common/common";
import { isAccountant, isAdmin } from "src/Common/function";

import CustomerAddModal from "./CustomerAddModal";
import LocationAddModal from "./components/LocationAddModal";
import RestoreDialog from "./components/RestoreDialog";

const CustomerAccountDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accountId } = useParams();

  const [account, setAccount] = useState(null);
  const [archivedLocations, setArchivedLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("active");
  const [search, setSearch] = useState("");

  const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [isArchiveCustomerOpen, setIsArchiveCustomerOpen] = useState(false);

  const canManage = isAdmin() || isAccountant();
  const activeLocations = (account?.customers || []).filter((c) => !c.isArchived);

  useEffect(() => {
    BranchService.getAll().then((response) => setBranches(response.data || []));
  }, []);

  useEffect(() => {
    if (accountId) loadAccount();
  }, [accountId]);

  const loadAccount = async () => {
    setIsLoading(true);
    try {
      const [accountRes, archivedRes, customersRes] = await Promise.all([
        AccountService.getOne(accountId),
        CustomerService.getArchivedByAccount(accountId),
        CustomerService.getAll(""),
      ]);
      setAccount(accountRes.data);
      setArchivedLocations(archivedRes.data || []);
      setCustomers(customersRes.data || []);
      dispatch(setCustomerName(accountRes.data?.name || ""));
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load customer");
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomer = async (data) => {
    try {
      await AccountService.updateAccount({ id: data.id, name: data.name });
      toast("Customer updated successfully");
      setIsEditCustomerOpen(false);
      await loadAccount();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to update customer");
    }
  };

  const archiveCustomer = async () => {
    try {
      await AccountService.archiveAccount(Number(accountId));
      toast("Customer archived");
      setIsArchiveCustomerOpen(false);
      navigate("/customer");
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to archive customer");
    }
  };

  const addLocations = async (data) => {
    try {
      for (const location of data.locations) {
        await CustomerService.saveCustomer({
          companyName: location.companyName,
          contact: location.contact,
          email: location.email,
          phone: location.phone,
          address: location.address,
          branch: location.branch,
          createdById: location.createdById,
          accountId: Number(accountId),
        });
      }
      toast("Location(s) saved successfully");
      setIsAddLocationOpen(false);
      await loadAccount();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to save location");
    }
  };

  const updateLocation = async (location) => {
    try {
      await CustomerService.updateCustomer(location);
      toast("Location updated successfully");
      setEditingLocation(null);
      await loadAccount();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to update location");
    }
  };

  const confirmArchiveLocation = async () => {
    if (!archiveTarget) return;
    try {
      await CustomerService.archiveCustomer(archiveTarget.id);
      toast("Location archived");
      setArchiveTarget(null);
      await loadAccount();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to archive location");
    }
  };

  const confirmRestoreLocation = async () => {
    if (!restoreTarget) return;
    try {
      await CustomerService.restoreCustomer(restoreTarget.id);
      toast("Location restored");
      setRestoreTarget(null);
      await loadAccount();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to restore location");
    }
  };

  const filterLocations = (list) => {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((loc) => loc.companyName?.toLowerCase().includes(term));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <Oval height={50} width={50} strokeWidth={2} color="#010D3D" secondaryColor="#80869E" visible />
      </div>
    );
  }

  const totalLocations = activeLocations.length + archivedLocations.length;
  const shownActive = filterLocations(activeLocations);
  const shownArchived = filterLocations(archivedLocations);

  return (
    <div className="w-full h-full bg-white px-6 py-5">
      {isEditCustomerOpen && (
        <CustomerAddModal
          account={account}
          accounts={[account]}
          branches={branches}
          onUpdateClicked={updateCustomer}
          onCancelClicked={() => setIsEditCustomerOpen(false)}
        />
      )}
      {isAddLocationOpen && (
        <LocationAddModal
          branches={branches}
          customers={customers}
          presetAccountId={Number(accountId)}
          onAddClicked={addLocations}
          onCancelClicked={() => setIsAddLocationOpen(false)}
        />
      )}
      {editingLocation && (
        <LocationAddModal
          branches={branches}
          customers={customers}
          customer={editingLocation}
          onUpateClicked={updateLocation}
          onCancelClicked={() => setEditingLocation(null)}
        />
      )}
      {archiveTarget && (
        <RestoreDialog
          variant="archive"
          title="Are you sure you want to archive this location?"
          description={[
            "Archived locations will no longer appear when creating new records.",
            "Historical records will remain unchanged.",
          ]}
          confirmLabel="Archive Location"
          onConfirm={confirmArchiveLocation}
          onCancel={() => setArchiveTarget(null)}
        />
      )}
      {restoreTarget && <RestoreDialog onConfirm={confirmRestoreLocation} onCancel={() => setRestoreTarget(null)} />}
      {isArchiveCustomerOpen && (
        <RestoreDialog
          variant="archive"
          title="Are you sure you want to archive this customer?"
          description={[
            <>
              Archiving a customer will also archive{" "}
              <span className="font-OpenSansSemiBold">ALL locations</span> associated with this customer. Historical
              records will remain available.
            </>,
          ]}
          details={[
            { label: "Customer", value: account?.name },
            { label: "Locations", value: activeLocations.length },
          ]}
          confirmLabel="Archive Customer"
          onConfirm={archiveCustomer}
          onCancel={() => setIsArchiveCustomerOpen(false)}
        />
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/customer")}>
            <img src={getImageUrl("/images/common/back_arrow.png")} className="w-[24px]" alt="back" />
            <span className="font-OpenSansBold text-[14px] text-subTitleColor">back</span>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsEditCustomerOpen(true)}
              className="h-[32px] px-3 rounded-[8px] bg-[#0065FD] inline-flex justify-center items-center gap-2 text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  d="M12.4994 11.3332C12.7755 11.3332 12.9994 11.557 12.9994 11.8332C12.9994 12.1093 12.7755 12.3332 12.4994 12.3332H6.49938C6.22323 12.3332 5.99938 12.1093 5.99938 11.8332C5.99938 11.557 6.22323 11.3332 6.49938 11.3332H12.4994Z"
                  fill="white"
                />
                <path
                  d="M11.3327 1.91536C11.3327 1.6727 11.2361 1.43985 11.0645 1.26823C10.8929 1.0966 10.6601 1 10.4174 1C10.1747 1 9.94185 1.09661 9.77022 1.26823L1.76306 9.27539C1.664 9.3744 1.59138 9.49707 1.55213 9.63151L1.07817 11.2539L2.70121 10.7806C2.83572 10.7414 2.95891 10.6687 3.05798 10.5697L11.0645 2.5625C11.2361 2.39092 11.3327 2.158 11.3327 1.91536ZM12.3327 1.91536C12.3327 2.42322 12.1306 2.91042 11.7715 3.26953L3.76502 11.2767C3.54696 11.4947 3.27724 11.654 2.98116 11.7402L1.06645 12.2988C0.923129 12.3406 0.770948 12.3437 0.626344 12.3066C0.481734 12.2696 0.349769 12.1941 0.244183 12.0885C0.138548 11.9829 0.0631595 11.8504 0.0260841 11.7057C-0.010928 11.5611 -0.00856058 11.409 0.0332455 11.2656L0.591839 9.35091C0.678205 9.05516 0.838116 8.78617 1.05603 8.56836L9.06319 0.561198C9.42235 0.20204 9.90943 4.37772e-06 10.4174 0C10.9253 0 11.4124 0.202037 11.7715 0.561198C12.1306 0.92035 12.3327 1.40748 12.3327 1.91536Z"
                  fill="white"
                />
                <path
                  d="M8.14586 1.47964C8.34112 1.28438 8.65763 1.28438 8.85289 1.47964L10.8529 3.47964C11.0482 3.6749 11.0482 3.99141 10.8529 4.18667C10.6576 4.38193 10.3411 4.38193 10.1459 4.18667L8.14586 2.18667C7.9506 1.99141 7.9506 1.6749 8.14586 1.47964Z"
                  fill="white"
                />
              </svg>{" "}
              Edit
            </button>
            <button
              onClick={() => setIsArchiveCustomerOpen(true)}
              className="h-[32px] px-3 rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 -outline-offset-1 outline-[#C9CEE2] flex items-center justify-center gap-2 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
                <path
                  d="M13.3333 1.16667C13.3333 1.07462 13.2587 1 13.1667 1H1.16667C1.07462 1 1 1.07462 1 1.16667V3.16667C1 3.25871 1.07462 3.33333 1.16667 3.33333H13.1667C13.2587 3.33333 13.3333 3.25871 13.3333 3.16667V1.16667ZM14.3333 3.16667C14.3333 3.811 13.811 4.33333 13.1667 4.33333H1.16667C0.522334 4.33333 0 3.811 0 3.16667V1.16667C0 0.522334 0.522334 0 1.16667 0H13.1667C13.811 0 14.3333 0.522334 14.3333 1.16667V3.16667Z"
                  fill="#1A2332"
                />
                <path
                  d="M1.33333 11.1667V3.83333C1.33333 3.55719 1.55719 3.33333 1.83333 3.33333C2.10948 3.33333 2.33333 3.55719 2.33333 3.83333V11.1667C2.33333 11.3877 2.42119 11.5996 2.57747 11.7559C2.73375 11.9121 2.94565 12 3.16667 12H11.1667C11.3877 12 11.5996 11.9121 11.7559 11.7559C11.9121 11.5996 12 11.3877 12 11.1667V3.83333C12 3.55719 12.2239 3.33333 12.5 3.33333C12.7761 3.33333 13 3.55719 13 3.83333V11.1667C13 11.6529 12.8067 12.1191 12.4629 12.4629C12.1191 12.8067 11.6529 13 11.1667 13H3.16667C2.68044 13 2.21426 12.8067 1.87044 12.4629C1.52663 12.1191 1.33333 11.6529 1.33333 11.1667Z"
                  fill="#1A2332"
                />
                <path
                  d="M8.5 6C8.77614 6 9 6.22386 9 6.5C9 6.77614 8.77614 7 8.5 7H5.83333C5.55719 7 5.33333 6.77614 5.33333 6.5C5.33333 6.22386 5.55719 6 5.83333 6H8.5Z"
                  fill="#1A2332"
                />
              </svg>{" "}
              Archive
            </button>
          </div>
        )}
      </div>

      {/* Customer Information */}
      <div className="bg-white shadow-sm rounded-[12px] overflow-hidden outline outline-1 -outline-offset-1 outline-borderColor mb-5">
        <div className="px-6 py-4 bg-backgroundColor border-b border-borderColor">
          <span className="text-[14px] font-OpenSansSemiBold text-titleColor capitalize tracking-[0.14px] leading-[20px]">
            Customer Information
          </span>
        </div>
        <div className="px-6 py-6 grid grid-cols-3 gap-8">
          <InfoField label="CUSTOMER NAME" value={account?.name} />
          <InfoField label="CREATED BY" value={account?.createdBy?.name || "—"} />
          <InfoField
            label="DATE ADDED"
            value={account?.createdAt ? formatDate(account.createdAt, "MMM D, YYYY") : "—"}
          />
        </div>
      </div>

      {/* Locations */}
      <div className="bg-white shadow-sm rounded-[12px] overflow-hidden outline outline-1 -outline-offset-1 outline-[#E5E8F3]">
        <div className="px-6 py-4 bg-[#F8F9FE] border-b border-[#E5E8F3] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332] capitalize leading-[20px] tracking-[0.14px]">
              Locations
            </span>
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-[16px] bg-[#EEF0F9] text-[12px] font-OpenSansSemiBold text-[#5F6477] capitalize leading-[18px] tracking-[0.12px]">
              {totalLocations}
            </span>
          </div>
          {canManage && (
            <button
              onClick={() => setIsAddLocationOpen(true)}
              className="h-[32px] px-3 rounded-[8px] bg-blueColor flex items-center gap-2 text-[14px] font-OpenSansSemiBold text-white capitalize leading-[20px] tracking-[0.14px]"
            >
              <PlusIcon /> Add Location
            </button>
          )}
        </div>

        {/* sub tabs + search */}
        <div className="px-6 pt-2 bg-[#FBFCFF] flex items-center justify-between border-b border-[#E5E7EB]">
          <div className="flex items-center">
            <SubTab label="Active" active={activeTab === "active"} onClick={() => setActiveTab("active")} />
            <SubTab label="Archived" active={activeTab === "archived"} onClick={() => setActiveTab("archived")} />
          </div>
          <div className="w-[264px] pb-2">
            <div className="relative">
              <FiSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#68778D]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="h-[32px] w-full pl-9 pr-3 rounded-[8px] bg-white border border-[#C9CEE2] text-[14px] font-OpenSansRegular leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6] focus:border-blueColor"
              />
            </div>
          </div>
        </div>

        {activeTab === "active" ? (
          <table className="w-full">
            <thead>
              <tr className="bg-[#F8F9FE] border-b border-[#E5E8F3]">
                <ThSm>Location Name</ThSm>
                <ThSm>Branch</ThSm>
                <ThSm>Contact Person</ThSm>
                <ThSm>Phone</ThSm>
                <ThSm className="text-right pr-6">Actions</ThSm>
              </tr>
            </thead>
            <tbody>
              {shownActive.length === 0 ? (
                <EmptyRow colSpan={5} message="No active locations." />
              ) : (
                shownActive.map((loc) => (
                  <tr
                    key={loc.id}
                    className="h-[54px] border-b border-[#E5E8F3] last:border-b-0 hover:bg-[#F8F9FE] cursor-pointer"
                    onClick={() => navigate(`/location/${loc.id}`)}
                  >
                    <td className="px-6 py-4 text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-[20px]">
                      {loc.companyName}
                    </td>
                    <td className="px-6 py-4 text-[14px] font-OpenSansRegular text-[#5F6477] leading-[20px]">
                      {loc.branch?.name || "—"}
                    </td>
                    <td className="px-6 py-4 text-[14px] font-OpenSansRegular text-[#5F6477] leading-[20px]">
                      {loc.contact || "—"}
                    </td>
                    <td className="px-6 py-4 text-[14px] font-OpenSansRegular text-[#5F6477] leading-[20px]">
                      {loc.phone || "—"}
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      {canManage && (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => setEditingLocation(loc)}
                            className="h-[32px] px-1 rounded-[6px] text-[14px] font-OpenSansSemiBold text-blueColor capitalize leading-[20px] tracking-[0.14px]"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setArchiveTarget(loc)}
                            className="h-[32px] px-1 rounded-[6px] text-[14px] font-OpenSansSemiBold text-[#7C829A] capitalize leading-[20px] tracking-[0.14px]"
                          >
                            Archive
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#F8F9FE] border-b border-[#E5E8F3]">
                <ThSm>Location Name</ThSm>
                <ThSm>Archived Date</ThSm>
                <ThSm>Archived By</ThSm>
                <ThSm className="text-right pr-6">Actions</ThSm>
              </tr>
            </thead>
            <tbody>
              {shownArchived.length === 0 ? (
                <EmptyRow colSpan={4} message="No archived locations." />
              ) : (
                shownArchived.map((loc) => (
                  <tr key={loc.id} className="h-[54px] border-b border-[#E5E8F3] last:border-b-0 hover:bg-[#F8F9FE]">
                    <td className="px-6 py-4 text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-[20px]">
                      {loc.companyName}
                    </td>
                    <td className="px-6 py-4 text-[14px] font-OpenSansRegular text-[#5F6477] leading-[20px]">
                      {loc.archivedAt ? formatDate(loc.archivedAt, "MMM D, YYYY") : "—"}
                    </td>
                    <td className="px-6 py-4 text-[14px] font-OpenSansRegular text-[#5F6477] leading-[20px]">
                      {loc.archivedBy?.name || "—"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {canManage && (
                        <button
                          onClick={() => setRestoreTarget(loc)}
                          className="h-[32px] px-1 rounded-[6px] text-[14px] font-OpenSansSemiBold text-blueColor capitalize leading-[20px] tracking-[0.14px]"
                        >
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

function InfoField({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-[12px] font-OpenSansSemiBold text-[#6B7280] uppercase tracking-[0.6px] leading-[18px]">
        {label}
      </p>
      <p className="text-[15px] font-OpenSansRegular text-[#111827] leading-[22.5px]">{value}</p>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
      <path
        d="M9.83333 4.66667C10.1095 4.66667 10.3333 4.89052 10.3333 5.16667C10.3333 5.44281 10.1095 5.66667 9.83333 5.66667H0.5C0.223858 5.66667 0 5.44281 0 5.16667C0 4.89052 0.223858 4.66667 0.5 4.66667H9.83333Z"
        fill="white"
      />
      <path
        d="M4.66667 9.83333V0.5C4.66667 0.223858 4.89052 0 5.16667 0C5.44281 0 5.66667 0.223858 5.66667 0.5V9.83333C5.66667 10.1095 5.44281 10.3333 5.16667 10.3333C4.89052 10.3333 4.66667 10.1095 4.66667 9.83333Z"
        fill="white"
      />
    </svg>
  );
}

function SubTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative p-4 text-[14px] font-OpenSansSemiBold capitalize leading-[20px] tracking-[0.14px] ${
        active ? "text-[#0058E5]" : "text-[#868DA6]"
      }`}
    >
      {label}
      {active && <span className="absolute left-0 -bottom-px w-full h-[2px] bg-[#0058E5]" />}
    </button>
  );
}

function ThSm({ children, className = "" }) {
  return (
    <th
      className={`h-[44px] px-6 py-3 text-left text-[12px] font-OpenSansSemiBold text-[#5F6477] leading-[18px] ${className}`}
    >
      {children}
    </th>
  );
}

function EmptyRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-8 text-center text-[14px] text-subTitleColor font-OpenSansRegular">
        {message}
      </td>
    </tr>
  );
}

export default CustomerAccountDetail;
